import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  type ArtistScore,
  DEFAULT_SHRINKAGE,
  type ShrinkageSettings,
  albumScore,
  artistScore,
  coverage,
} from "@trackmark/core";
import { RankTable, rankTableStyles as table } from "../design/RankTable.js";
import { RatingStrip } from "../design/RatingStrip.js";
import styles from "./Artist.module.css";

/**
 * The artist page (design/README.md §9).
 *
 * *"Leads with the score, not the covers."* The hero is a full-width `well` carrying one number
 * at 72px and everything needed to read it honestly beside it — the raw mean, the sample size,
 * the coverage the score is drawn from. The covers come after, and the songs after those.
 *
 * The one thing this page must never do is print a mean and call it a score. Below the threshold
 * there is **no score at all**: a progress rule, the count, and a line saying why — the same
 * refusal artist rankings makes, in the one place where a big empty number would be most tempting.
 */

export interface ArtistAlbum {
  id: number;
  title: string;
  year: number | null;
  isTracked: boolean;
  cohesion: number | null;
  art: string | null;
  coreCount: number;
  ratedCore: number;
  tracks: { rating: number | null; isCore: boolean; durationMs: number | null }[];
}

export interface ArtistSong {
  id: number;
  title: string;
  album: string;
  albumId: number;
  discNo: number;
  trackNo: number | null;
  year: number | null;
  score: number | null;
  /** Library-wide, not a position in this artist's list. */
  rank: number | null;
  art: string | null;
}

export interface ArtistPayload {
  artist: { id: number; name: string; ratedCount: number; raw: number | null; ownedCount: number };
  collection: { totalRated: number; mean: number | null };
  settings: ShrinkageSettings;
  rank: number | null;
  qualifiedCount: number;
  albums: ArtistAlbum[];
  songs: ArtistSong[];
}

export const SONG_TABS = [
  { tab: "score", label: "BY SCORE" },
  { tab: "album", label: "BY ALBUM" },
  { tab: "year", label: "BY YEAR" },
  { tab: "unrated", label: "UNRATED FIRST" },
] as const;

export type SongTab = (typeof SONG_TABS)[number]["tab"];

const SONG_COLUMNS = "56px 24px 1fr 230px 116px 44px 52px";

/** Album order inside a record: the order the record has, not the alphabet. */
const byTrack = (a: ArtistSong, b: ArtistSong) =>
  a.album.localeCompare(b.album) ||
  a.discNo - b.discNo ||
  (a.trackNo ?? Number.MAX_SAFE_INTEGER) - (b.trackNo ?? Number.MAX_SAFE_INTEGER) ||
  a.title.localeCompare(b.title);

const byScore = (a: ArtistSong, b: ArtistSong) =>
  // Unrated pins last rather than sorting as zero — it is an absence, not a low opinion.
  (b.score ?? -1) - (a.score ?? -1) || a.title.localeCompare(b.title);

function sortSongs(songs: ArtistSong[], tab: SongTab): ArtistSong[] {
  const sorted = [...songs];
  if (tab === "score") return sorted.sort(byScore);
  if (tab === "album") return sorted.sort(byTrack);
  if (tab === "year") {
    // Oldest first: a discography reads forwards. Songs with no year go last rather than to 1900.
    return sorted.sort((a, b) => (a.year ?? 9999) - (b.year ?? 9999) || byTrack(a, b));
  }
  return sorted.sort((a, b) => Number(a.score !== null) - Number(b.score !== null) || byTrack(a, b));
}

export function Artist(props: ArtistPayload & { tab?: SongTab }) {
  const { artist, collection, settings, rank, qualifiedCount, albums } = props;
  const [tab, setTab] = useState<SongTab>(props.tab ?? "score");

  const score: ArtistScore = useMemo(
    () =>
      artistScore(
        // The payload carries ingredients, not ratings: a sample with this mean and this size is
        // all shrinkage reads, and it keeps this number identical to the rankings table's.
        artist.raw === null ? [] : Array.from({ length: artist.ratedCount }, () => artist.raw as number),
        collection,
        settings ?? DEFAULT_SHRINKAGE,
      ),
    [artist.raw, artist.ratedCount, collection, settings],
  );

  const songs = useMemo(() => sortSongs(props.songs, tab), [props.songs, tab]);
  const cov = coverage(artist.ratedCount, artist.ownedCount);

  const scoredAlbums = useMemo(
    () =>
      albums
        .map((album) => ({
          album,
          // An untracked record has no known track list, so it has no score — the same refusal the
          // library tile makes rather than scoring the fraction we happen to hold.
          result: album.isTracked ? albumScore(album.tracks, album.cohesion) : { complete: false, score: null },
        }))
        .sort((a, b) => (b.result.score ?? -1) - (a.result.score ?? -1) || a.album.title.localeCompare(b.album.title)),
    [albums],
  );
  const complete = scoredAlbums.filter((a) => a.result.complete).length;

  return (
    <div className={styles.page}>
      <div className={styles.hero} data-testid="artist-hero">
        <div className={styles.identity}>
          <div className={styles.eyebrow} data-testid="artist-eyebrow">
            ARTIST
            {rank === null
              ? " · NOT YET RANKED"
              : ` · RANK ${rank} OF ${qualifiedCount.toLocaleString()} QUALIFIED`}
          </div>
          <div className={styles.name} data-testid="artist-name">
            {artist.name}
          </div>
        </div>

        <div className={styles.numbers}>
          {score.qualified ? (
            <div>
              <div className={styles.score} data-testid="artist-score">
                {score.score!.toFixed(2)}
              </div>
              <div className={styles.scoreLabel}>SCORE · ADJUSTED FOR SAMPLE SIZE</div>
            </div>
          ) : (
            /*
             * No score at all, and the reason in words. A mean of four songs looks exactly like a
             * mean of a hundred and forty at 72px, which is why the app will not print one.
             */
            <div className={styles.unqualified} data-testid="artist-unqualified">
              <div className={styles.unqualifiedCount}>
                {artist.ratedCount} / {settings.artistThreshold} SONGS RATED
              </div>
              <div
                className={styles.unqualifiedRule}
                style={{ width: `${Math.min(100, (artist.ratedCount / settings.artistThreshold) * 100)}%` }}
              />
              <div className={styles.unqualifiedWhy}>
                A MEAN OF {artist.ratedCount} {artist.ratedCount === 1 ? "SONG" : "SONGS"} IS NOT A CLAIM THIS APP
                WILL MAKE ON YOUR BEHALF. RATE {settings.artistThreshold - artist.ratedCount} MORE.
              </div>
            </div>
          )}

          <div className={styles.facts}>
            <div>
              <b data-penalised={score.penalised || undefined} data-testid="artist-raw">
                {artist.raw === null ? "—" : artist.raw.toFixed(2)}
              </b>{" "}
              <span>RAW MEAN</span>
            </div>
            <div>
              <b>{artist.ratedCount.toLocaleString()}</b> <span>SONGS RATED</span>
            </div>
            <div>
              <b>{albums.length}</b>{" "}
              <span>
                {albums.length === 1 ? "ALBUM" : "ALBUMS"} · {complete} COMPLETE
              </span>
            </div>
          </div>

          <div className={styles.coverage}>
            <div className={styles.coverageRow}>
              <div className={styles.coverageTrack}>
                <div
                  className={styles.coverageFill}
                  data-low={cov.low || undefined}
                  style={{ width: `${Math.round(cov.ratio * 100)}%` }}
                />
              </div>
              <div className={styles.coveragePercent} data-testid="artist-coverage">
                {Math.round(cov.ratio * 100)}%
              </div>
            </div>
            <div className={styles.coverageLabel}>
              COVERAGE · {artist.ratedCount.toLocaleString()} OF {artist.ownedCount.toLocaleString()} OWNED SONGS RATED
            </div>
          </div>
        </div>
      </div>

      {scoredAlbums.length > 0 && (
        <div className={styles.albums}>
          <div className={styles.sectionLabel}>ALBUMS · BY SCORE</div>
          <div className={styles.covers} data-testid="artist-albums">
            {scoredAlbums.map(({ album, result }) => (
              <Link
                key={album.id}
                to="/album/$albumId"
                params={{ albumId: String(album.id) }}
                className={styles.cover}
                data-testid="artist-album"
                data-complete={result.complete ? "true" : "false"}
              >
                <div className={styles.coverArtWrap}>
                  {album.art ? (
                    <img className={styles.coverArt} src={album.art} alt="" />
                  ) : (
                    <div className={styles.coverFallback}>{album.title}</div>
                  )}
                </div>
                <div className={styles.coverTitle}>{album.title}</div>
                <div className={styles.coverMeta}>
                  <span className={styles.coverYear}>{album.year ?? "—"}</span>
                  {/* Never a number for a record that is short of a rating: a partial score is
                      the defect, not a smaller version of the score. */}
                  {result.complete ? (
                    <span className={styles.coverScore}>{result.score!.toFixed(2)}</span>
                  ) : (
                    <span className={styles.coverInc} data-testid="album-incomplete">
                      INC
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className={styles.songs}>
        <div className={styles.songsHead}>
          <div className={styles.sectionLabel} data-testid="songs-label">
            THEIR SONGS · {props.songs.length.toLocaleString()} · RANK IS LIBRARY-WIDE
          </div>
          <div className={styles.tabs}>
            {SONG_TABS.map((t) => (
              <button
                key={t.tab}
                type="button"
                className={styles.tab}
                data-active={t.tab === tab || undefined}
                data-testid={`songs-tab-${t.tab}`}
                onClick={() => setTab(t.tab)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <RankTable
          rows={songs}
          columns={SONG_COLUMNS}
          rowKey={(s) => s.id}
          // The flash band is the unrated one the whole app uses; nothing else marks a row here.
          rowProps={(s) => ({ "data-state": s.score === null ? "unrated" : undefined, "data-testid": "artist-song" })}
          header={
            <>
              <div>RANK</div>
              <div />
              <div>TITLE</div>
              <div>ALBUM</div>
              <div>RATING</div>
              <div style={{ textAlign: "right" }}>SCR</div>
              <div style={{ textAlign: "right" }}>YEAR</div>
            </>
          }
          renderRow={(song) => (
            <>
              <div className={table.rank} data-unranked={song.rank === null || undefined}>
                {song.rank ?? "—"}
              </div>
              {song.art ? (
                <img className={table.art} src={song.art} alt="" />
              ) : (
                <div className={table.artFallback}>{song.album.slice(0, 8)}</div>
              )}
              <div className={table.title}>{song.title}</div>
              <Link
                to="/album/$albumId"
                params={{ albumId: String(song.albumId) }}
                className={table.secondaryLink}
                data-testid="song-album-link"
              >
                {song.album}
              </Link>
              <RatingStrip value={song.score} />
              {/* An em dash, not a nought: the song has no score, it does not have a low one. */}
              <div className={table.score}>{song.score ?? "—"}</div>
              <div className={table.meta}>{song.year ?? "—"}</div>
            </>
          )}
        />
      </div>
    </div>
  );
}
